/**
 * Mage Spell Effect - Lightning and AOE spell visual effects for Taoist mage attacks
 * Features:
 * - Lightning bolt from caster to target
 * - Ground impact ring
 * - Energy pillar at target location
 * - Chain lightning for AOE attacks
 */

import * as THREE from 'three';
import { EventBus } from '@/core/events/EventBus';

interface LightningBolt {
  startX: number;
  startY: number;
  startZ: number;
  endX: number;
  endY: number;
  endZ: number;
  age: number;
  maxAge: number;
  segments: THREE.Vector3[];
  intensity: number;
}

interface ImpactRing {
  x: number;
  y: number;
  z: number;
  age: number;
  maxAge: number;
  maxRadius: number;
}

interface EnergyPillar {
  x: number;
  y: number;
  z: number;
  age: number;
  maxAge: number;
  height: number;
}

const MAX_LIGHTNING_BOLTS = 50;
const MAX_IMPACT_RINGS = 30;
const MAX_ENERGY_PILLARS = 30;

// Shader for lightning bolts
const lightningVertexShader = `
  attribute float alpha;
  varying float vAlpha;
  varying vec3 vPosition;

  void main() {
    vAlpha = alpha;
    vPosition = position;
    vec4 mvPosition = modelViewMatrix * vec4(position, 1.0);
    gl_Position = projectionMatrix * mvPosition;
  }
`;

const lightningFragmentShader = `
  uniform vec3 color;
  uniform float time;
  varying float vAlpha;
  varying vec3 vPosition;

  void main() {
    // Core lightning color with flickering
    float flicker = 0.8 + 0.2 * sin(time * 30.0 + vPosition.y * 10.0);
    vec3 coreColor = color * flicker;

    // Add white hot core
    vec3 finalColor = mix(coreColor, vec3(1.0, 1.0, 1.0), 0.3);

    gl_FragColor = vec4(finalColor, vAlpha);
  }
`;

// Shader for impact rings
const ringVertexShader = `
  varying vec2 vUv;

  void main() {
    vUv = uv;
    gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
  }
`;

const ringFragmentShader = `
  uniform float progress;
  uniform vec3 color;
  varying vec2 vUv;

  void main() {
    // Distance from center
    vec2 center = vec2(0.5, 0.5);
    float dist = distance(vUv, center) * 2.0;

    // Ring effect - expands outward
    float ringPos = progress;
    float ringWidth = 0.15;
    float ring = 1.0 - smoothstep(ringPos - ringWidth, ringPos, dist)
               + smoothstep(ringPos, ringPos + ringWidth, dist);
    ring = 1.0 - ring;

    // Fade based on progress
    float fade = 1.0 - progress;

    // Inner glow
    float innerGlow = (1.0 - dist) * (1.0 - progress * 0.5) * 0.3;

    float alpha = (ring + innerGlow) * fade;

    gl_FragColor = vec4(color, alpha);
  }
`;

// Shader for energy pillar
const pillarVertexShader = `
  varying vec2 vUv;
  varying float vHeight;

  void main() {
    vUv = uv;
    vHeight = position.y;
    gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
  }
`;

const pillarFragmentShader = `
  uniform float progress;
  uniform float time;
  uniform vec3 color;
  uniform float maxHeight;
  varying vec2 vUv;
  varying float vHeight;

  void main() {
    // Vertical fade
    float heightRatio = vHeight / maxHeight;
    float verticalFade = 1.0 - heightRatio;

    // Animate texture
    float wave = sin(vHeight * 20.0 - time * 15.0) * 0.5 + 0.5;
    float pulse = sin(time * 10.0) * 0.2 + 0.8;

    // Edge fade
    float edgeFade = 1.0 - abs(vUv.x - 0.5) * 2.0;
    edgeFade = pow(edgeFade, 0.5);

    // Combine
    float alpha = verticalFade * edgeFade * wave * pulse;

    // Fade out based on progress
    float progressFade = progress < 0.7 ? 1.0 : (1.0 - (progress - 0.7) / 0.3);
    alpha *= progressFade;

    // Add white core
    vec3 finalColor = mix(color, vec3(1.0), edgeFade * 0.5);

    gl_FragColor = vec4(finalColor, alpha * 0.8);
  }
`;

export class MageSpellEffect {
  private scene: THREE.Scene;
  private time = 0;

  // Lightning bolts
  private lightningBolts: LightningBolt[] = [];
  private lightningMaterial: THREE.ShaderMaterial;
  private lightningLines: THREE.Line[] = [];

  // Impact rings
  private impactRings: ImpactRing[] = [];
  private ringGeometry: THREE.PlaneGeometry;
  private ringMaterials: THREE.ShaderMaterial[] = [];
  private ringMeshes: THREE.Mesh[] = [];

  // Energy pillars
  private energyPillars: EnergyPillar[] = [];
  private pillarGeometry: THREE.CylinderGeometry;
  private pillarMaterials: THREE.ShaderMaterial[] = [];
  private pillarMeshes: THREE.Mesh[] = [];

  // Colors
  private primaryColor = new THREE.Color(0x00ffff);  // Cyan
  private secondaryColor = new THREE.Color(0x88ffff); // Light cyan

  constructor(scene: THREE.Scene) {
    this.scene = scene;

    // Initialize lightning material
    this.lightningMaterial = new THREE.ShaderMaterial({
      vertexShader: lightningVertexShader,
      fragmentShader: lightningFragmentShader,
      uniforms: {
        color: { value: this.primaryColor },
        time: { value: 0 },
      },
      transparent: true,
      blending: THREE.AdditiveBlending,
      depthWrite: false,
    });

    // Initialize ring geometry
    this.ringGeometry = new THREE.PlaneGeometry(1, 1);

    // Initialize pillar geometry
    this.pillarGeometry = new THREE.CylinderGeometry(0.3, 0.5, 1, 8, 4, true);

    // Pre-create ring meshes
    for (let i = 0; i < MAX_IMPACT_RINGS; i++) {
      const material = new THREE.ShaderMaterial({
        vertexShader: ringVertexShader,
        fragmentShader: ringFragmentShader,
        uniforms: {
          progress: { value: 0 },
          color: { value: this.primaryColor },
        },
        transparent: true,
        blending: THREE.AdditiveBlending,
        depthWrite: false,
        side: THREE.DoubleSide,
      });
      this.ringMaterials.push(material);

      const mesh = new THREE.Mesh(this.ringGeometry, material);
      mesh.rotation.x = -Math.PI / 2;
      mesh.visible = false;
      this.scene.add(mesh);
      this.ringMeshes.push(mesh);
    }

    // Pre-create pillar meshes
    for (let i = 0; i < MAX_ENERGY_PILLARS; i++) {
      const material = new THREE.ShaderMaterial({
        vertexShader: pillarVertexShader,
        fragmentShader: pillarFragmentShader,
        uniforms: {
          progress: { value: 0 },
          time: { value: 0 },
          color: { value: this.primaryColor },
          maxHeight: { value: 1 },
        },
        transparent: true,
        blending: THREE.AdditiveBlending,
        depthWrite: false,
        side: THREE.DoubleSide,
      });
      this.pillarMaterials.push(material);

      const mesh = new THREE.Mesh(this.pillarGeometry, material);
      mesh.visible = false;
      this.scene.add(mesh);
      this.pillarMeshes.push(mesh);
    }

    // Setup event listeners
    this.setupEventListeners();
  }

  private setupEventListeners(): void {
    EventBus.on('spell:cast', (data) => {
      // Staff tip is higher than caster position
      const staffTipY = data.casterY + 1.2;

      // Spawn lightning bolt
      this.spawnLightningBolt(
        data.casterX, staffTipY, data.casterZ,
        data.targetX, data.targetY + 0.5, data.targetZ
      );

      // Spawn impact ring at target
      this.spawnImpactRing(data.targetX, data.targetY + 0.1, data.targetZ, data.spellType === 'aoe' ? 3.0 : 1.5);

      // Spawn energy pillar at target
      this.spawnEnergyPillar(data.targetX, data.targetY, data.targetZ, data.spellType === 'aoe' ? 4.0 : 2.5);

      // For AOE attacks, spawn additional lightning bolts to nearby area
      if (data.spellType === 'aoe' && data.targetCount > 1) {
        const additionalBolts = Math.min(data.targetCount - 1, 4);
        for (let i = 0; i < additionalBolts; i++) {
          const angle = (i / additionalBolts) * Math.PI * 2 + Math.random() * 0.5;
          const radius = 1.5 + Math.random() * 1.5;
          const offsetX = Math.cos(angle) * radius;
          const offsetZ = Math.sin(angle) * radius;

          // Delayed secondary bolts
          setTimeout(() => {
            this.spawnLightningBolt(
              data.targetX, data.targetY + 2, data.targetZ,
              data.targetX + offsetX, data.targetY + 0.5, data.targetZ + offsetZ
            );
            this.spawnImpactRing(data.targetX + offsetX, data.targetY + 0.1, data.targetZ + offsetZ, 1.0);
          }, 50 + i * 30);
        }
      }
    });
  }

  private spawnLightningBolt(
    startX: number, startY: number, startZ: number,
    endX: number, endY: number, endZ: number
  ): void {
    if (this.lightningBolts.length >= MAX_LIGHTNING_BOLTS) {
      // Remove oldest
      const oldest = this.lightningBolts.shift();
      if (oldest) {
        const line = this.lightningLines.shift();
        if (line) {
          this.scene.remove(line);
          line.geometry.dispose();
        }
      }
    }

    // Generate jagged lightning path
    const segments = this.generateLightningPath(
      new THREE.Vector3(startX, startY, startZ),
      new THREE.Vector3(endX, endY, endZ)
    );

    const bolt: LightningBolt = {
      startX, startY, startZ,
      endX, endY, endZ,
      age: 0,
      maxAge: 0.3 + Math.random() * 0.1,
      segments,
      intensity: 1.0,
    };
    this.lightningBolts.push(bolt);

    // Create line geometry
    const geometry = new THREE.BufferGeometry().setFromPoints(segments);

    // Add alpha attribute
    const alphas = new Float32Array(segments.length);
    for (let i = 0; i < segments.length; i++) {
      alphas[i] = 1.0;
    }
    geometry.setAttribute('alpha', new THREE.BufferAttribute(alphas, 1));

    const line = new THREE.Line(geometry, this.lightningMaterial.clone());
    this.scene.add(line);
    this.lightningLines.push(line);
  }

  private generateLightningPath(start: THREE.Vector3, end: THREE.Vector3): THREE.Vector3[] {
    const points: THREE.Vector3[] = [];
    const numSegments = 12 + Math.floor(Math.random() * 8);

    const direction = end.clone().sub(start);
    const length = direction.length();
    direction.normalize();

    // Create perpendicular vectors for displacement
    const up = new THREE.Vector3(0, 1, 0);
    const perp1 = new THREE.Vector3().crossVectors(direction, up).normalize();
    const perp2 = new THREE.Vector3().crossVectors(direction, perp1).normalize();

    for (let i = 0; i <= numSegments; i++) {
      const t = i / numSegments;
      const basePoint = start.clone().lerp(end, t);

      if (i > 0 && i < numSegments) {
        // Add jagged displacement
        const displacement = (1 - Math.abs(t - 0.5) * 2) * 0.3; // Max in middle
        const offset1 = (Math.random() - 0.5) * displacement * length;
        const offset2 = (Math.random() - 0.5) * displacement * length;

        basePoint.add(perp1.clone().multiplyScalar(offset1));
        basePoint.add(perp2.clone().multiplyScalar(offset2));
      }

      points.push(basePoint);
    }

    return points;
  }

  private spawnImpactRing(x: number, y: number, z: number, maxRadius: number): void {
    if (this.impactRings.length >= MAX_IMPACT_RINGS) {
      this.impactRings.shift();
    }

    this.impactRings.push({
      x, y, z,
      age: 0,
      maxAge: 0.5,
      maxRadius,
    });
  }

  private spawnEnergyPillar(x: number, y: number, z: number, height: number): void {
    if (this.energyPillars.length >= MAX_ENERGY_PILLARS) {
      this.energyPillars.shift();
    }

    this.energyPillars.push({
      x, y, z,
      age: 0,
      maxAge: 0.6,
      height,
    });
  }

  update(deltaTime: number): void {
    this.time += deltaTime;

    // Update lightning material time
    this.lightningMaterial.uniforms.time.value = this.time;

    // Update lightning bolts
    for (let i = this.lightningBolts.length - 1; i >= 0; i--) {
      const bolt = this.lightningBolts[i];
      bolt.age += deltaTime;

      if (bolt.age >= bolt.maxAge) {
        // Remove expired bolt
        this.lightningBolts.splice(i, 1);
        const line = this.lightningLines.splice(i, 1)[0];
        if (line) {
          this.scene.remove(line);
          line.geometry.dispose();
          if (line.material instanceof THREE.Material) {
            line.material.dispose();
          }
        }
        continue;
      }

      // Update line opacity
      const line = this.lightningLines[i];
      if (line && line.geometry) {
        const progress = bolt.age / bolt.maxAge;
        const fade = 1 - progress;

        // Flicker effect
        const flicker = Math.random() > 0.3 ? 1 : 0.5;

        const alphas = line.geometry.getAttribute('alpha');
        if (alphas) {
          for (let j = 0; j < alphas.count; j++) {
            alphas.setX(j, fade * flicker);
          }
          alphas.needsUpdate = true;
        }

        // Occasionally regenerate path for flicker
        if (Math.random() < 0.3) {
          const newSegments = this.generateLightningPath(
            new THREE.Vector3(bolt.startX, bolt.startY, bolt.startZ),
            new THREE.Vector3(bolt.endX, bolt.endY, bolt.endZ)
          );
          line.geometry.setFromPoints(newSegments);
        }
      }
    }

    // Update impact rings
    for (let i = this.impactRings.length - 1; i >= 0; i--) {
      const ring = this.impactRings[i];
      ring.age += deltaTime;

      if (ring.age >= ring.maxAge) {
        this.impactRings.splice(i, 1);
        continue;
      }

      const mesh = this.ringMeshes[i];
      const material = this.ringMaterials[i];
      if (mesh && material) {
        mesh.visible = true;
        mesh.position.set(ring.x, ring.y, ring.z);

        const progress = ring.age / ring.maxAge;
        const scale = ring.maxRadius * 2;
        mesh.scale.set(scale, scale, 1);

        material.uniforms.progress.value = progress;
      }
    }

    // Hide unused ring meshes
    for (let i = this.impactRings.length; i < MAX_IMPACT_RINGS; i++) {
      this.ringMeshes[i].visible = false;
    }

    // Update energy pillars
    for (let i = this.energyPillars.length - 1; i >= 0; i--) {
      const pillar = this.energyPillars[i];
      pillar.age += deltaTime;

      if (pillar.age >= pillar.maxAge) {
        this.energyPillars.splice(i, 1);
        continue;
      }

      const mesh = this.pillarMeshes[i];
      const material = this.pillarMaterials[i];
      if (mesh && material) {
        mesh.visible = true;

        const progress = pillar.age / pillar.maxAge;

        // Grow quickly, then shrink
        let heightScale: number;
        if (progress < 0.2) {
          heightScale = progress / 0.2;
        } else {
          heightScale = 1.0;
        }

        const currentHeight = pillar.height * heightScale;
        mesh.position.set(pillar.x, pillar.y + currentHeight / 2, pillar.z);
        mesh.scale.set(1, currentHeight, 1);

        material.uniforms.progress.value = progress;
        material.uniforms.time.value = this.time;
        material.uniforms.maxHeight.value = currentHeight;
      }
    }

    // Hide unused pillar meshes
    for (let i = this.energyPillars.length; i < MAX_ENERGY_PILLARS; i++) {
      this.pillarMeshes[i].visible = false;
    }
  }

  dispose(): void {
    // Dispose lightning
    for (const line of this.lightningLines) {
      this.scene.remove(line);
      line.geometry.dispose();
      if (line.material instanceof THREE.Material) {
        line.material.dispose();
      }
    }
    this.lightningMaterial.dispose();
    this.lightningBolts = [];
    this.lightningLines = [];

    // Dispose rings
    for (const mesh of this.ringMeshes) {
      this.scene.remove(mesh);
    }
    for (const material of this.ringMaterials) {
      material.dispose();
    }
    this.ringGeometry.dispose();
    this.impactRings = [];

    // Dispose pillars
    for (const mesh of this.pillarMeshes) {
      this.scene.remove(mesh);
    }
    for (const material of this.pillarMaterials) {
      material.dispose();
    }
    this.pillarGeometry.dispose();
    this.energyPillars = [];
  }
}
